import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Paper,
  Stack,
  Typography,
  CircularProgress,
  FormControlLabel,
  Checkbox,
  Button,
  IconButton,
  Snackbar,
  Alert,
  Autocomplete,
  TextField,
  Chip,
  Tooltip,
} from "@mui/material";


import RemoveIcon from "@mui/icons-material/Remove";
import AddIcon from "@mui/icons-material/Add";

import DownloadIcon from "@mui/icons-material/Download";
import PrintIcon from "@mui/icons-material/Print";
import FunctionsIcon from "@mui/icons-material/Functions";
import FingerprintIcon from "@mui/icons-material/Fingerprint";
import SyncIcon from "@mui/icons-material/Sync";
import EditTimeDialog from "../components/EditTimeDialog";
import DateFilterBar from "../components/DateFilterBar";
import ReportSettingsLauncher from "../components/ReportSettingsPage";

import FiltersToolbar from "../components/attendance/FiltersToolbar";
import AttendanceTable from "../components/attendance/AttendanceTable";
import { useAttendanceData } from "../hooks/useAttendanceData";
import {
  encodeSegmentsToCellText,
  decodeCellTextToSegments,
  makeDirtyKey,
  normalizeBaseUrl,
  calculateRulesMinutesForDay,
  getDayKeyFromIso,
} from "../components/attendance/attendanceUtils";
import dayjs from "dayjs";
import {
  normalizeSch,
  getCellHours,
  getTotalHours,
  scheduleMinutes,
  formatSignedHours,
} from "../components/attendance/attendanceUtils";

import axios from "axios";

// Hr access to visits report !!
import { useNavigate } from "react-router-dom";
// import { allowedUsers } from "../components/constants";
import AssignmentIcon from "@mui/icons-material/Assignment";

//legend colors 
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import Popover from "@mui/material/Popover";

import { apiFetch } from "../utils/apiFetch";




export default function AllAttendanceNewPage() {
  const isDark = localStorage.getItem("saved_theme_id") === "2";

  const [dlgCellSegments, setDlgCellSegments] = useState([]);

  const [fullMonth, setFullMonth] = useState(false);
  const STORAGE_KEY = "attendance_report_settings";

  const defaultReportSettings = useMemo(
    () => ({
      attendance: false,    //so waterfall flows through to leave=true correctly,waterfall hits attendance=true first → returns rows 1->7, never reaches leave branch
      leave: false,
      custom: false,
      all: false,
      reportType: "details",
      customFields: [],
    }),
    [],
  );

  const [reportSettings, setReportSettings] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      return saved ? JSON.parse(saved) : defaultReportSettings;
    } catch {
      return defaultReportSettings;
    }
  });

  const [hideWeekends, setHideWeekends] = useState(true);
  const [hideRulesInfos, setHideRulesInfos] = useState(false);

  const [selectedEmps, setSelectedEmps] = useState([]);
  const [selectedLeaves, setSelectedLeaves] = useState([]);
  const [selectedTimingFilter, setSelectedTimingFilter] = useState(null);
  const [machineInfos, setMachineInfos] = useState({});
  const [selectedMachines, setSelectedMachines] = useState([]);
  const [machineActionLoading, setMachineActionLoading] = useState(false);
// legend color 
  const [legendAnchor, setLegendAnchor]= useState(null);
  const legendOpen = Boolean(legendAnchor);

  const [orgName, setOrgName] = useState(() => localStorage.getItem("saved_orgname") || "");

//! just for testing message for machine turn off or on ! 
  const [machineStatusMsg, setMachineStatusMsg] = useState("");

  // report visitis 
  const navigate = useNavigate();
  // const username = localStorage.getItem("saved_username") || "";
  // const showAttendanceDropdown = allowedUsers.includes(username);

  // ---- Collapse/hide panels toggle ----
  const HIDE_PANELS_KEY = "attendance_hide_panels";
  const [hidePanels, setHidePanels] = useState(() => {
    try {
      return localStorage.getItem(HIDE_PANELS_KEY) === "1";
    } catch {
      return false;
    }
  });

  const toggleHidePanels = useCallback(() => {
    setHidePanels((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(HIDE_PANELS_KEY, next ? "1" : "0");
      } catch {}
      return next;
    });
  }, []);

  // ---- Per-user persisted filters ----
  const getEmpKeyForStorage = () => {
    try {
      return localStorage.getItem("shared_empid") || "default";
    } catch {
      return "default";
    }
  };
  const FILTERS_KEY = `attendance_filters_${getEmpKeyForStorage()}`;

  // Raw restored values (ids only) — matched against option lists once they load
  const [pendingRestoredEmpKeys, setPendingRestoredEmpKeys] = useState(null);
  const [pendingRestoredLeaveIds, setPendingRestoredLeaveIds] = useState(null);
  const [pendingRestoredTimingId, setPendingRestoredTimingId] = useState(null);
  const restoredDatesRef = useRef(null);
  const hasRestoredRef = useRef(false);

  // Auto-run "Apply" (Get & Sync) once, as soon as the page has real dates
  // to work with — no user click needed.
  const hasAutoRunRef = useRef(false);
  const [datesReady, setDatesReady] = useState(false); 

  //* To save the last clicks for select machine ! 
  const [pendingRestoredMachineKeys, setPendingRestoredMachineKeys] = useState(null);
// ----end Collapse/hide panels toggle ----

// Restore saved filters once on mount 
  useEffect(() => {
    if (hasRestoredRef.current) return;
    hasRestoredRef.current = true;
    try {
      const raw = localStorage.getItem(FILTERS_KEY);
      if (!raw) return;
      const saved = JSON.parse(raw);
      if (saved?.empKeys) setPendingRestoredEmpKeys(saved.empKeys);
      if (saved?.leaveIds) setPendingRestoredLeaveIds(saved.leaveIds);
      if (saved?.timingId !== undefined) setPendingRestoredTimingId(saved.timingId);
      if (saved?.fromDate && saved?.toDate) {
        restoredDatesRef.current = {
          from: dayjs(saved.fromDate),
          to: dayjs(saved.toDate),
          fullMonth: !!saved.fullMonth,
        };
      }
      if (saved?.machineKeys) setPendingRestoredMachineKeys(saved.machineKeys);
    } catch (e) {
      console.error("Failed to restore saved filters:", e);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  useEffect(() => {
    const fetchMachineInfo = async () => {
      try {
        const rawBase = localStorage.getItem("saved_sett_api_url");
        const API_BASE_URL = normalizeBaseUrl(rawBase);
        if (!API_BASE_URL) return;
        const response = await apiFetch(`${API_BASE_URL}/api/machines-infos`);
        const data = await response.json();
        if (data && typeof data === "object") {
          setMachineInfos(data);
          localStorage.setItem("saved_sett_machine_infos", JSON.stringify(data));
        }
      } catch (error) {
        console.error("Error fetching machine data:", error);
      }
    };
    fetchMachineInfo();
  }, []);

  const machineOptions = useMemo(
    () =>
      Object.entries(machineInfos || {}).map(([key, val]) => ({
        key,
        label: String(val?.NickName || key),
      })),
    [machineInfos],
  );

  const handleGetMachine = useCallback(async () => {
    setMachineActionLoading(true);
    try {
      const rawBase = localStorage.getItem("saved_sett_api_url");
      const API_BASE_URL = normalizeBaseUrl(rawBase);
      if (!API_BASE_URL) return;
      const machineKeys = selectedMachines.map((m) => m.key);
      await axios.get(`${API_BASE_URL}/api/machine-attendance`, {
        params: machineKeys.length ? { machines: machineKeys } : undefined,
        withCredentials: true,
      });
    } catch (error) {
      console.error("Error fetching machine attendance:", error);
    } finally {
      setMachineActionLoading(false);
    }
  }, [selectedMachines]);

  const [locationSyncLoading, setLocationSyncLoading] = useState(false);
//*the original 
  // const handleLocationSync = useCallback(async () => {
  //   setLocationSyncLoading(true);
  //   try {
  //     const rawBase = localStorage.getItem("saved_sett_api_url");
  //     const API_BASE_URL = normalizeBaseUrl(rawBase);
  //     if (!API_BASE_URL) return;
  //     await axios.get(`${API_BASE_URL}/api/location/sync`, { withCredentials: true });

  //     setToast({open: true, msg: "Sync Completed successfully.", sev: "success"});

  //   } catch (error) {
  //     console.error("Error syncing location:", error);
  //     setToast({open: true, msg: "Sync failed.", sev: "error"});
  //   } finally {
  //     setLocationSyncLoading(false);
  //   }
  // }, []);

 

  const [dlgOpen, setDlgOpen] = useState(false);
  const [dlgEmpKey, setDlgEmpKey] = useState(null);
  const [dlgIso, setDlgIso] = useState(null);
  const [dlgEmpName, setDlgEmpName] = useState("");
  const [dlgEmpId, setDlgEmpId] = useState(null);
  const [dlgAddress, setDlgAddress] = useState("");
  const [dlgInitialTimes, setDlgInitialTimes] = useState(["", ""]);
  const [dlgStampModules, setDlgStampModules] = useState("");

  const [dirtyMap, setDirtyMap] = useState({});
  const [toast, setToast] = useState({ open: false, msg: "", sev: "success" });
  const [refreshTrigger, setRefreshTrigger] = useState(0); //21/5/26

  const containerRef = useRef(null);
  const printAreaRef = useRef(null);


  const {
    loading,
    machineLoading,
    monthData,  // used by grid
    setMonthData, //used when editing cells
    restLeavesByEmp,
    leaveOptionsCache,
    safeRange,
    visibleDays,
    employeeOptions,
    filteredKeys,
    totalEmployees,
    fromDate,
    toDate,
    appliedFromDate,
    appliedToDate,
    setFromDate,
    setToDate,
    handleApplyDates,
    allMonths,
    setAllMonths,
    appliedAllMonths,
    timingFilterOptions,
    reportGridFields,
  } = useAttendanceData({
    hideWeekends,
    selectedEmps,
    selectedLeaves,
    selectedTimingFilter,
  });


  // ⬇️⬇️⬇️ PASTE ALL OF STEP 3's useEffects RIGHT HERE ⬇️⬇️⬇️

  // Apply restored dates as soon as setters are available (before DateFilterBar's
  // own mount-effect can default fromDate/toDate to "today")
 useEffect(() => {
  const restored = restoredDatesRef.current;
  if (restored) {
    setFromDate(restored.from);
    setToDate(restored.to);
    setAllMonths(restored.fullMonth);
    handleApplyDates(restored.from, restored.to);
    restoredDatesRef.current = null;
  }
  setDatesReady(true);   // ← ADD THIS LINE
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [setFromDate, setToDate, setAllMonths, handleApplyDates]);

  // Match restored employee keys against employeeOptions once they load
  useEffect(() => {
    if (!pendingRestoredEmpKeys || !employeeOptions?.length) return;
    const matched = employeeOptions.filter((opt) =>
      pendingRestoredEmpKeys.includes(String(opt.key)),
    );
    if (matched.length) setSelectedEmps(matched);
    setPendingRestoredEmpKeys(null);
  }, [pendingRestoredEmpKeys, employeeOptions]);

  // ✅  MACHINE-MATCHING EFFECT Save the last click  ✅
  useEffect(() => {
    if (!pendingRestoredMachineKeys || !machineOptions?.length) return;
    const matched = machineOptions.filter((opt) =>
      pendingRestoredMachineKeys.includes(String(opt.key)),
    );
    if (matched.length) setSelectedMachines(matched);
    setPendingRestoredMachineKeys(null);
  }, [pendingRestoredMachineKeys, machineOptions]);

  // Match restored leave ids against leaveOptionsCache once they load
  useEffect(() => {
    if (!pendingRestoredLeaveIds || !leaveOptionsCache?.length) return;
    const matched = leaveOptionsCache.filter((opt) =>
      pendingRestoredLeaveIds.includes(String(opt.leaveid)),
    );
    if (matched.length) setSelectedLeaves(matched);
    setPendingRestoredLeaveIds(null);
  }, [pendingRestoredLeaveIds, leaveOptionsCache]);

  // Match restored leave ids against leaveOptionsCache once they load
  useEffect(() => {
    if (!pendingRestoredLeaveIds || !leaveOptionsCache?.length) return;
    const matched = leaveOptionsCache.filter((opt) =>
      pendingRestoredLeaveIds.includes(String(opt.leaveid)),
    );
    if (matched.length) setSelectedLeaves(matched);
    setPendingRestoredLeaveIds(null);
  }, [pendingRestoredLeaveIds, leaveOptionsCache]);

  // Match restored timing filter id against timingFilterOptions once they load
  useEffect(() => {
    if (pendingRestoredTimingId == null || !timingFilterOptions?.length) return;
    const matched = timingFilterOptions.find(
      (opt) => String(opt?.id ?? opt?.key ?? opt?.label) === String(pendingRestoredTimingId),
    );
    if (matched) setSelectedTimingFilter(matched);
    setPendingRestoredTimingId(null);
  }, [pendingRestoredTimingId, timingFilterOptions]);

  // Persist filters (employees, leaves, timing, dates) whenever they change
  useEffect(() => {
    try {
      const payload = {
        empKeys: (selectedEmps || []).map((e) => String(e.key)),
        leaveIds: (selectedLeaves || []).map((l) => String(l.leaveid)),
        timingId:
          selectedTimingFilter != null
            ? String(
                selectedTimingFilter?.id ??
                  selectedTimingFilter?.key ??
                  selectedTimingFilter?.label,
              )
            : null,
        fromDate: appliedFromDate ? appliedFromDate.format("YYYY-MM-DD") : null,
        toDate: appliedToDate ? appliedToDate.format("YYYY-MM-DD") : null,
        fullMonth: !!appliedAllMonths,
        machineKeys: (selectedMachines || []).map((m) => String(m.key)),
      };
      localStorage.setItem(FILTERS_KEY, JSON.stringify(payload));
    } catch (e) {
      console.error("Failed to save filters:", e);
    }
  }, [
    selectedEmps,
    selectedLeaves,
    selectedTimingFilter,
    appliedFromDate,
    appliedToDate,
    appliedAllMonths,
    selectedMachines,
  ]);

  //  END PASTE ⬆️

  

  //* sync process without pschdly 
  // const handleLocationSync = useCallback(async () => {
  //     setLocationSyncLoading(true);
  //     try{
  //       const rawBase = localStorage.getItem("saved_sett_api_url");
  //       const API_BASE_URL = normalizeBaseUrl(rawBase);
  //       if (!API_BASE_URL) return;
        
  //       const from = safeRange.start.format("YYYY-MM-DD");
  //       const to = safeRange.end.format("YYYY-MM-DD");

  //       await axios.get(`${API_BASE_URL}/api/location/sync`, { 
  //         params: { fromdate: from, todate: to },
  //         withCredentials: true
  //       });

  //       setToast({ open: true, msg: "Sync completed successfully.", sev: "success"});
  //  }  catch (error) {
  //       console.error("Error syncing location:", error);
  //       setToast({ open: true, msg: "Sync failed.", sev: "error" });
  //     } finally{
  //       setLocationSyncLoading(false);
  //       }
  //   }, [safeRange]);

   //* sync process (locationConfirmation and pschdly)
  const handleLocationSync = useCallback(async () => {
    setLocationSyncLoading(true);
    try {
      const rawBase = localStorage.getItem("saved_sett_api_url");
      const API_BASE_URL = normalizeBaseUrl(rawBase);
      if (!API_BASE_URL) return;
      
      const from = safeRange.start.format("YYYY-MM-DD");
      const to = safeRange.end.format("YYYY-MM-DD");

      // Run both syncs in parallel
      const [locationRes, scheduleRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/api/location/sync`, { 
          params: { fromdate: from, todate: to },
          withCredentials: true
        }),
        axios.get(`${API_BASE_URL}/api/schedule/sync`, { 
          params: { fromdate: from, todate: to },
          withCredentials: true
        }),
      ]);

      setToast({ open: true, msg: "Location & Schedule sync completed successfully.", sev: "success" });

    } catch (error) {
      console.error("Error syncing:", error);
      setToast({ open: true, msg: "Sync failed.", sev: "error" });
    } finally {
      setLocationSyncLoading(false);
    }
  }, [safeRange]);

  

  //* getMachine & sync process (locationConfirmation and pschdly)& applyFilter
  const handleGetAndSync = useCallback(async () => {
    setMachineActionLoading(true);
    setLocationSyncLoading(true);
    try{
      const rawBase = localStorage.getItem("saved_sett_api_url");
      const API_BASE_URL = normalizeBaseUrl(rawBase);
      if (!API_BASE_URL) return;

      // Apply first - commits the currently-picked dates. Use fromDate/toDate
      // directly below (not safeRange/appliedFromDate) since state updates
      // from this call haven't landed yet within this same function run.
      handleApplyDates(fromDate, toDate);

      //Step1:get from machine (only if machines are selected)
      const from = fromDate.format("YYYY-MM-DD");
      const to = toDate.format("YYYY-MM-DD");
      //   if (selectedMachines.length > 0 ) {
    //   const machineKeys = selectedMachines.map((m) => m.key);
    //   await axios.get(`${API_BASE_URL}/api/machine-attendance`, {
    //     params: machineKeys.length ? { machines: machineKeys , fromdate: from, todate: to} : undefined,
    //     withCredentials: true,
    //   });
    // }
    if (selectedMachines.length > 0) {
        const machineKeys = selectedMachines.map((m) => m.key);
        let elapsed = 0;

        console.log("🔄 Connecting to machine...");
        const statusTimer = setInterval(() => {
          elapsed += 1;
          console.log(`⏳ Still connecting to machine... (${elapsed}s)`);
        }, 1000);

        try {
          const machineRes = await axios.get(`${API_BASE_URL}/api/machine-attendance`, {
            params: machineKeys.length ? { machines: machineKeys, fromdate: from, todate: to } : undefined,
            withCredentials: true,
            timeout: 12000,
          });
          clearInterval(statusTimer);

          if (machineRes?.data?.status === "error") {
          console.error("⚠ Machine error:", machineRes.data.message);
          setToast({ open: true, msg: machineRes.data.message || "Machine not responding.", sev: "error" });
          return;
        }

        console.log("✅ Connection ended — machine responded successfully and is online.");
        } catch (machineErr) {
          clearInterval(statusTimer);
          const timedOutOnClient = machineErr.code === "ECONNABORTED";
          const backendMsg = machineErr?.response?.data?.message;
          const msg = timedOutOnClient
            ? "⚠ Connection ended — no response after 12 seconds. Machine appears turned off."
            : backendMsg
            ? `⚠ Connection ended with error: ${backendMsg}`
            : "⚠ Connection ended — could not reach the machine. It may be turned off.";
          console.error(msg);
          setToast({ open: true, msg, sev: "error" });
          return;
        }
      }

    // uncomment this step2: 1/6
      //Step 2 : sync location + schedule in parallel  26/5/26
     
      await Promise.all([
      axios.get(`${API_BASE_URL}/api/location/sync`, {
        params: { fromdate: from, todate: to },
        withCredentials: true,
      }),
      axios.get(`${API_BASE_URL}/api/schedule/sync`, {
        params: { fromdate: from, todate: to },
        withCredentials: true,
      }),
      // sync pleave for approve leave cound and local 
      axios.get(`${API_BASE_URL}/api/pleave/sync`, {   // ✅ new
          params: { fromdate: from, todate: to },
          withCredentials: true,
        }),
      // sync pvisitClient for approve visit cloud and local   
      axios.get(`${API_BASE_URL}/api/visit/sync`, {   // ✅ new
      params: { fromdate: from, todate: to },
      withCredentials: true,
    }),  
    ]);


    //Step 3 : refresh the table 
    handleApplyDates(fromDate, toDate);

    setToast({ open: true, msg: "Get & Sync completed successfully.", sev:"success"});
  } catch (error) {
    console.error("Error in get & sync:", error);
    setToast({open: true, msg: "Get & Sync failed.", sev: "error"});
  } finally {
    setMachineActionLoading(false);
    setLocationSyncLoading(false);
  }
  },  [selectedMachines, fromDate, toDate, handleApplyDates]);

//! fires handleGetAndSync() automatically once the page has real dates
//!  to work with (since it depends on fromDate/toDate being set first, not null).
 useEffect(() => {
  if (hasAutoRunRef.current) return;
  if (!datesReady) return;   // ← CHANGED — wait for restoration to actually finish
  hasAutoRunRef.current = true;
  handleGetAndSync();
}, [datesReady, fromDate, toDate, handleGetAndSync]);   // ← ADD datesReady here



  //* for export to excel 
  const handleExportTimings = useCallback(async () => {
    try {
      const rawBase = localStorage.getItem("saved_sett_api_url");
      const API_BASE_URL = normalizeBaseUrl(rawBase);

      const from  = appliedFromDate.format("DD/MM/YYYY");
      const to    = appliedToDate.format("DD/MM/YYYY");
      const month = appliedFromDate.format("M");

      const fromDisplay = appliedFromDate.format("DD/MM/YYYY");
      const toDisplay   = appliedToDate.format("DD/MM/YYYY");

      // selectedEmps holds employee option objects — pull out their keys
      // (EmpIds) and join into a comma-separated string for the backend.
      // Empty selection = export everyone, matching existing behavior.
      const empIds = (selectedEmps || []).map((e) => e.key).join(",");

      const res = await axios.get(`${API_BASE_URL}/api/export-timings`, {
        params: { from, to, month, empIds: empIds || undefined },
        withCredentials: true,
        responseType: "blob",
      });

      const url  = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href  = url;
      link.setAttribute(
        "download",
        `Timings_${fromDisplay.replace(/\//g, "-")}_${toDisplay.replace(/\//g, "-")}.xlsx`
      );
      document.body.appendChild(link);
      link.click();
      link.remove();

    } catch (err) {
      console.error("Export failed:", err);

      if (err?.response?.status === 404) {
          setToast({ open: true, msg: "No data found for the selected date range.", sev: "warning" });
      } else {
          setToast({ open: true, msg: "Export to Excel failed!", sev: "error" });
      }
    }
  }, [appliedFromDate, appliedToDate, selectedEmps]);
  
//  const handleExportTimings = useCallback(async () => {
//   try {
//     const rawBase = localStorage.getItem("saved_sett_api_url");
//     const API_BASE_URL = normalizeBaseUrl(rawBase);

//     // Sent to the backend — must match DateIn's stored format (YYYY-MM-DD),
//     // since export_ctrl.py compares this string directly against DateIn in SQL.
//     const from  = appliedFromDate.format("DD/MM/YYYY");
//     const to    = appliedToDate.format("DD/MM/YYYY");
//     const month = appliedFromDate.format("M");

//     // Only for display purposes (filename) — DD/MM/YYYY as you want it shown.
//     const fromDisplay = appliedFromDate.format("DD/MM/YYYY");
//     const toDisplay   = appliedToDate.format("DD/MM/YYYY");

//     const res = await axios.get(`${API_BASE_URL}/api/export-timings`, {
//       params: { from, to, month, empId: selectedEmpId || undefined },
//       withCredentials: true,
//       responseType: "blob",
//     });

//     const url  = window.URL.createObjectURL(new Blob([res.data]));
//     const link = document.createElement("a");
//     link.href  = url;
//     link.setAttribute(
//       "download",
//       `Timings_${fromDisplay.replace(/\//g, "-")}_${toDisplay.replace(/\//g, "-")}.xlsx`
//     );
//     document.body.appendChild(link);
//     link.click();
//     link.remove();

//   } catch (err) {
//     console.error("Export failed:", err);

//     if (err?.response?.status === 404) {
//         setToast({ open: true, msg: "No data found for the selected date range.", sev: "warning" });
//     } else {
//         setToast({ open: true, msg: "Export to Excel failed!", sev: "error" });
//     }
//   }
// }, [appliedFromDate, appliedToDate]);
    
  
  const displayedKeys = useMemo(() => {
    if (!reportSettings.leave) return filteredKeys;        // #if leave data is off show all employees normally 

    const WORKING_PREFIXES = ["working", "dawam", "دوام"];
    const isNonWorkingLine = (line) => {
      const t = line.trim().toLowerCase();
      return t && t !== "divider_line" && !WORKING_PREFIXES.some((p) => t.startsWith(p));
    };

    return filteredKeys.filter((empKey) => {          // #if leave data is on filter employees 
      const emp = monthData[empKey];
      if (!emp?.days) return false;

      return visibleDays.some((iso) => {
        const dayCell = emp.days[iso];
        if (!dayCell) return false;

        if (dayCell.leaveLabel) return true;      //#has a leave label , keep employee 

        const cellText = String(dayCell.time || "").trim();
        if (!cellText) return false;

        if (decodeCellTextToSegments(cellText, leaveOptionsCache).some((seg) => seg.mode === "LEAVE")) {  //#has a leave seg , keep employee
          return true;
        }

        return cellText.split("\n").some(isNonWorkingLine);    //#has non-working line, keep employee
      });
    });
  }, [filteredKeys, reportSettings.leave, monthData, visibleDays, leaveOptionsCache]);
  // const displayedKeys = useMemo(() => {
  //   if (!reportSettings.leave) return filteredKeys;

  //   // Leave Data ON → show only employees who have leave balance data
  //   return filteredKeys.filter((empKey) => {
  //     const emp = monthData[empKey];
  //     const empId = String(emp?.empId ?? empKey);
  //     const items = restLeavesByEmp?.[empId]?.items;
  //     return Array.isArray(items) && items.length > 0;
  //   });

  // }, [filteredKeys, reportSettings.leave, monthData, restLeavesByEmp]);

  const openEditDialog = useCallback(
  (empKey, iso) => {
    const emp = monthData[empKey];
    const cell = emp?.days?.[iso] || null;

    const cellText = String(cell?.time || "");
    const segs = decodeCellTextToSegments(cellText, leaveOptionsCache);

    const flatTimes = [];
    segs.forEach((s) => {
      if (s.time_in) flatTimes.push(s.time_in);
      if (s.time_out) flatTimes.push(s.time_out);
    });

    setDlgInitialTimes(flatTimes.length ? flatTimes : ["", ""]);
    setDlgOpen(true);
    setDlgEmpKey(empKey);
    setDlgIso(iso);
    setDlgEmpName(emp?.employee || String(empKey));
    setDlgEmpId(emp?.empId ?? empKey);
    setDlgAddress(cell?.address || "");
    setDlgCellSegments(segs); 
    setDlgStampModules(cell?.stampModules || cell?._src?.schedule?.stampModules || "");
  },
  [monthData, leaveOptionsCache],
);
  const closeEditDialog = useCallback(() => {
  setDlgOpen(false);
  setDlgEmpKey(null);
  setDlgIso(null);
  setDlgEmpName("");
  setDlgEmpId(null);
  setDlgAddress("");
  setDlgInitialTimes(["", ""]);
  setDlgCellSegments([]);  
  setDlgStampModules("");
}, []);

  // const handleDlgSaveLocal = useCallback(
  //   (segments) => {
  //     if (!dlgEmpKey || !dlgIso) return;

  //     const segs = Array.isArray(segments) ? segments : [];
  //     const cellText = encodeSegmentsToCellText(segs, leaveOptionsCache);

  //     // Derive sumtimesh from the first in-time to last out-time of work segments
  //     // so "Real Schedule" and "Addition/Retention Hours" rows stay in sync with the edit.
  //     const workSegs = segs.filter((s) => {
  //       const mode = String(s?.mode || "").toUpperCase();
  //       return mode === "WORK" || String(s?.leaveid || "").toUpperCase() === "DAWAM";
  //     });
  //     const derivedSumtimesh =
  //       workSegs.length && workSegs[0]?.time_in && workSegs[workSegs.length - 1]?.time_out
  //         ? `${workSegs[0].time_in} ~ ${workSegs[workSegs.length - 1].time_out}`
  //         : null;

  //     setMonthData((prev) => {
  //       const next = { ...prev };
  //       const emp = next[dlgEmpKey]
  //         ? { ...next[dlgEmpKey] }
  //         : { employee: dlgEmpName, days: {} };

  //       const days = { ...(emp.days || {}) };
  //       const existing = days[dlgIso] || {};

  //       const updatedDay = {
  //         ...existing,
  //         time: cellText,
  //         sumtimesh: derivedSumtimesh ?? existing.sumtimesh,
  //         status: existing.status ?? 3,
  //         address: existing.address ?? dlgAddress ?? "",
  //       };

  //       const ruleRows = emp?.ruleRows ?? [];
  //       const calc = calculateRulesMinutesForDay(
  //         updatedDay,
  //         dlgIso,
  //         ruleRows,
  //         leaveOptionsCache,
  //       );

  //       days[dlgIso] = {
  //         ...updatedDay,
  //         scheduleAfterRulesMinutes: calc.totalMinutes,
  //         scheduleAfterRulesHours: calc.totalHours,
  //         scheduleAfterRulesBreakdown: calc.breakdown,
  //         scheduleAfterRulesWeighted: calc.weighted,
  //       };

  //       emp.days = days;
  //       if (!emp.employee) emp.employee = dlgEmpName;
  //       if (emp.empId == null) emp.empId = dlgEmpId;

  //       next[dlgEmpKey] = emp;
  //       return next;
  //     });

  //     const dk = makeDirtyKey(dlgEmpId ?? dlgEmpKey, dlgIso);
  //     setDirtyMap((prev) => ({
  //       ...prev,
  //       [dk]: { empId: dlgEmpId ?? dlgEmpKey, date: dlgIso, segments: segs },
  //     }));

  //     setToast({ open: true, msg: "Saved locally.", sev: "success" });
  //     closeEditDialog();
  //   },
  //   [
  //     dlgEmpKey,
  //     dlgIso,
  //     dlgEmpName,
  //     dlgEmpId,
  //     dlgAddress,
  //     leaveOptionsCache,
  //     closeEditDialog,
  //     setMonthData,
  //   ],
  // );

    // !!!!!New handleDlgSAve

  const handleDlgSaveLocal = useCallback(
    (segments) => {
      if (!dlgEmpKey || !dlgIso) return;

      const segs = Array.isArray(segments) ? segments : [];

      // ── Validation ──────────────────────────────────────────────
      const toMin = (hhmm) => {
        const parts = String(hhmm || "").split(":");
        const h = parseInt(parts[0], 10);
        const m = parseInt(parts[1] || "0", 10);
        if (isNaN(h) || isNaN(m)) return null;
        return h * 60 + m;
      };

      const validSegs = segs.filter((s) => {
        const start = toMin(s.time_in);
        const end = toMin(s.time_out);
        return start !== null && end !== null && end > start;
      });

      const workSegs = validSegs.filter((s) => s.mode !== "LEAVE");
      const leaveSegs = validSegs.filter((s) => s.mode === "LEAVE");

      // Rule 1: check if it is  fully inside a work segment
      for (const leave of leaveSegs) {
        const leaveStart = toMin(leave.time_in);
        const leaveEnd = toMin(leave.time_out);
        const overlapsWork = workSegs.some((work) => {
          return leaveStart < toMin(work.time_out) && toMin(work.time_in) < leaveEnd;
        });
        if (overlapsWork) {
          setToast({
            open: true,
            msg: `❌ Leave ${leave.time_in}~${leave.time_out} overlaps a work segment. You must split: Work 08:00~09:00 + Leave 09:00~10:00 + Work 10:00~16:00`,
            sev: "error",
          });
          return;
        }
      }

      // Rule 2: work segments must not overlap
      // for (let i = 0; i < workSegs.length; i++) {
      //   for (let j = i + 1; j < workSegs.length; j++) {
      //     const a = { start: toMin(workSegs[i].time_in), end: toMin(workSegs[i].time_out) };
      //     const b = { start: toMin(workSegs[j].time_in), end: toMin(workSegs[j].time_out) };
      //     if (a.start < b.end && b.start < a.end) {
      //       setToast({
      //         open: true,
      //         msg: `❌ Work segments overlap: ${workSegs[i].time_in}~${workSegs[i].time_out} and ${workSegs[j].time_in}~${workSegs[j].time_out}`,
      //         sev: "error",
      //       });
      //       return;
      //     }
      //   }
      // }

      // Rule 3: leave segments must not overlap
      for (let i = 0; i < leaveSegs.length; i++) {
        for (let j = i + 1; j < leaveSegs.length; j++) {
          const a = { start: toMin(leaveSegs[i].time_in), end: toMin(leaveSegs[i].time_out) };
          const b = { start: toMin(leaveSegs[j].time_in), end: toMin(leaveSegs[j].time_out) };
          if (a.start < b.end && b.start < a.end) {
            setToast({
              open: true,
              msg: `❌ Leave segments overlap: ${leaveSegs[i].time_in}~${leaveSegs[i].time_out} and ${leaveSegs[j].time_in}~${leaveSegs[j].time_out}`,
              sev: "error",
            });
            return;
          }
        }
      }
      // ── End Validation ───────────────────────────────────────────

      const cellText = encodeSegmentsToCellText(segs, leaveOptionsCache);

      setMonthData((prev) => {
        const next = { ...prev };
        const emp = next[dlgEmpKey]
          ? { ...next[dlgEmpKey] }
          : { employee: dlgEmpName, days: {} };

        const days = { ...(emp.days || {}) };
        const existing = days[dlgIso] || {};

        days[dlgIso] = {
          ...existing,
          time: cellText,
          status: existing.status ?? 3,
          address: existing.address ?? dlgAddress ?? "",
        };

        emp.days = days;
        if (!emp.employee) emp.employee = dlgEmpName;
        if (emp.empId == null) emp.empId = dlgEmpId;

        next[dlgEmpKey] = emp;
        return next;
      });

      const dk = makeDirtyKey(dlgEmpId ?? dlgEmpKey, dlgIso);
      setDirtyMap((prev) => ({
        ...prev,
        [dk]: { empId: dlgEmpId ?? dlgEmpKey, date: dlgIso, segments: segs },
      }));

      setToast({ open: true, msg: "Saved locally.", sev: "success" });
      closeEditDialog();

    
    //   // ← simulate clicking APPLY button to refresh data
      handleApplyDates(appliedFromDate, appliedToDate);
      
    // },
    // sync pschdly only after HR save, THEN refresh grid 8-6-26
      // const rawBase = localStorage.getItem("saved_sett_api_url");
      // const API_BASE_URL = normalizeBaseUrl(rawBase);
      // if (API_BASE_URL) {
      //   const from = appliedFromDate.format("YYYY-MM-DD");
      //   const to   = appliedToDate.format("YYYY-MM-DD");
      //   axios.get(`${API_BASE_URL}/api/schedule/sync`, {
      //     params: { fromdate: from, todate: to },
      //     withCredentials: true,
      //   }).then(() => {
      //     // ✅ apply AFTER sync completes
      //     handleApplyDates(appliedFromDate, appliedToDate);
      //   }).catch((e) => {
      //     console.error("Schedule sync failed:", e);
      //     // refresh anyway even if sync fails
      //     handleApplyDates(appliedFromDate, appliedToDate);
      //   });
      // } else {
      //   handleApplyDates(appliedFromDate, appliedToDate);
      // }
    },
    [
      dlgEmpKey,
      dlgIso,
      dlgEmpName,
      dlgEmpId,
      dlgAddress,
      leaveOptionsCache,
      closeEditDialog,
      setMonthData,       // Step 1 — Local update (instant)
      handleApplyDates,   // ← add 21/5/26
      appliedFromDate,    // ← add
      appliedToDate,      // ← add
    ],
  );

  const cleanCellText = (text, leaveOptionsCache) => {
  let result = String(text || "");

  // remove "working:"
  result = result.replace(/\bworking\s*:\s*/gi, "");

  // remove divider
  result = result.replace(/devider_line/gi, "");

  const LEAVE_ABBR = {
    "Annual": "AN",
    "Sickness Paid": "SP",
    "Sick": "SK",
    "Sickness Unpaid": "SU",
    "Casual": "CA",
    "Emergency": "EM",
    "Unpaid": "UP",
  };

  // from leaveOptionsCache (dynamic)
  (leaveOptionsCache || []).forEach((opt) => {
    const label = String(opt?.label || "").trim();
    if (!label) return;

    const abbr =
      opt?.shortName || opt?.abbr || opt?.code ||
      label.split(" ").map(w => w[0]).join("").toUpperCase();

    const escaped = label.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    result = result.replace(new RegExp(escaped, "gi"), abbr);
  });

  // fallback static
  Object.keys(LEAVE_ABBR).forEach((name) => {
    const abbr = LEAVE_ABBR[name];
    const escaped = name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    result = result.replace(new RegExp(escaped, "gi"), abbr);
  });

  return result.trim();
};

  const handleCalculateRules = useCallback(async () => {
    try {
      const rawBase = localStorage.getItem("saved_sett_api_url");
      const API_BASE_URL = normalizeBaseUrl(rawBase);

      if (!API_BASE_URL) {
        setToast({
          open: true,
          msg: "API base URL is missing.",
          sev: "error",
        });
        return;
      }

      const empIds = filteredKeys
        .map((empKey) => monthData?.[empKey]?.empId ?? empKey)
        .filter((v) => v !== null && v !== undefined && String(v).trim() !== "")
        .map((v) => String(v));

      const params = {
        from: safeRange.start.format("YYYY-MM-DD"),
        to: safeRange.end.format("YYYY-MM-DD"),
      };

      if (empIds.length) {
        params.empids = empIds.join(",");
      }

      const res = await axios.get(`${API_BASE_URL}/api/calculaterules`, {
        params,
        withCredentials: true,
      });

      const apiRules =
        res?.data && typeof res.data === "object" ? res.data : {};

      setMonthData((prev) => {
        const next = { ...prev };

        Object.keys(next).forEach((empKey) => {
          const emp = next[empKey];
          if (!emp?.days) return;

          const empId = String(emp?.empId ?? empKey);
          const empRulesMap = apiRules?.[empId] || {};
          const mergedRuleRows = Object.values(empRulesMap).flat();
          const nextDays = { ...emp.days };

          Object.keys(nextDays).forEach((iso) => {
            const dayObj = nextDays[iso] || {};

            const existingRuleMap =
              dayObj?.rulestime && typeof dayObj.rulestime === "object"
                ? { ...dayObj.rulestime }
                : {};

            const existingRulesMap =
              dayObj?.rulesMap && typeof dayObj.rulesMap === "object"
                ? { ...dayObj.rulesMap }
                : {};

            const mergedRulesMap = {
              ...existingRulesMap,
              ...existingRuleMap,
            };

            const dayKey = getDayKeyFromIso(iso);

            let normalizedRuleText = String(
              mergedRulesMap?.[dayKey] ?? dayObj?.rulestime?.[dayKey] ?? "",
            )
              .trim()
              .replace(/\s*\|\s*/g, " ~ ")
              .replace(/\s*~\s*/g, " ~ ");

            if (!normalizedRuleText) {
              const matchedRow = mergedRuleRows.find(
                (r) => String(r?.DayList || "").trim() === dayKey && r?.WorkTime,
              );
              if (matchedRow) {
                normalizedRuleText = String(matchedRow.WorkTime)
                  .trim()
                  .replace(/\s*\|\s*/g, " ~ ")
                  .replace(/\s*~\s*/g, " ~ ");
              }
            }

            if (!normalizedRuleText) normalizedRuleText = "—";

            const calc = calculateRulesMinutesForDay(
              {
                ...dayObj,
                rulestime: {
                  ...mergedRulesMap,
                  [dayKey]: normalizedRuleText,
                },
              },
              iso,
              mergedRuleRows,
              leaveOptionsCache,
            );

            nextDays[iso] = {
              ...dayObj,
              rulesMap: mergedRulesMap,
              rulestime: {
                ...mergedRulesMap,
                [dayKey]: normalizedRuleText,
              },
              scheduleAfterRulesMinutes: calc.totalMinutes,
              scheduleAfterRulesHours: calc.totalHours,
              scheduleAfterRulesBreakdown: calc.breakdown,
              scheduleAfterRulesWeighted: calc.weighted,
            };
          });

          next[empKey] = {
            ...emp,
            days: nextDays,
            ruleRows: mergedRuleRows,
          };
        });

        return next;
      });

      setToast({
        open: true,
        msg: "Rules calculated successfully.",
        sev: "success",
      });
    } catch (error) {
      console.error(error);
      setToast({
        open: true,
        msg: "Failed to calculate rules.",
        sev: "error",
      });
    }
  }, [filteredKeys, monthData, safeRange, leaveOptionsCache, setMonthData]);


const handlePrintGrid = useCallback(() => {
  const visibleFieldKeys = (() => {
    if (!reportSettings || reportSettings.all) {
      return new Set([
        "attendance",
        "realSchedule",
        "supposedSchedule",
        "workingHours",
        "additionRetentionHours",
        "additionRetentionAfterRules",
        "workingHoursAfterRules",
      ]);
    }

    if (reportSettings.attendance) {
      return new Set([
        "attendance",
        "realSchedule",
        "supposedSchedule",
        "workingHours",
        "additionRetentionHours",
        "additionRetentionAfterRules",
        "workingHoursAfterRules",
      ]);
    }

    if (reportSettings.leave) {     // // print only attendance column
      return new Set(["attendance"]);
    }

    if (reportSettings.custom) {
      return new Set(reportSettings.customFields || []);  // print only checked fields
    }

    return new Set([
      "attendance",
      "realSchedule",
      "supposedSchedule",
      "workingHours",
      "additionRetentionHours",
      "additionRetentionAfterRules",
      "workingHoursAfterRules",
    ]);
  })();

  const columns = [
    visibleFieldKeys.has("attendance") && {
      key: "attendance",
      label: "Attendance",
    },
    !hideRulesInfos &&
      visibleFieldKeys.has("realSchedule") && {
        key: "realSchedule",
        label: "Real Schedule",
      },
    !hideRulesInfos &&
      visibleFieldKeys.has("supposedSchedule") && {
        key: "supposedSchedule",
        label: "Supposed Schedule",
      },
    !hideRulesInfos &&
      visibleFieldKeys.has("workingHours") && {
        key: "workingHours",
        label: "Hours",
      },
    !hideRulesInfos &&
      visibleFieldKeys.has("additionRetentionHours") && {
        key: "additionRetentionHours",
        label: "Addition/Retention Hours",
      },
    !hideRulesInfos &&
      visibleFieldKeys.has("additionRetentionAfterRules") && {
        key: "additionRetentionAfterRules",
        label: "Addition / Retention After Rules",
      },
    !hideRulesInfos &&
      visibleFieldKeys.has("workingHoursAfterRules") && {
        key: "workingHoursAfterRules",
        label: "Hours After Rules",
      },
  ].filter(Boolean);

  const LEAVE_ABBR = {
    Annual: "AN",
    "Annual Leave": "AN",
    "Sickness Paid": "SP",
    Sick: "SK",
    "Sickness Unpaid": "SU",
    Casual: "CA",
    Emergency: "EM",
    Unpaid: "UP",
    Maternity: "MT",
    Paternity: "PT",
    Compensatory: "CO",
    Marriage: "MR",
    Hajj: "HJ",
    Study: "ST",
    Official: "OF",
  };

  const escapeHtml = (value) =>
    String(value ?? "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;");

  const getLeaveAbbreviation = (leaveName) => {
    const raw = String(leaveName || "").trim();
    if (!raw) return "";

    const directMatch = Object.keys(LEAVE_ABBR).find(
      (k) => k.toLowerCase() === raw.toLowerCase(),
    );
    if (directMatch) return LEAVE_ABBR[directMatch];

    const match = (leaveOptionsCache || []).find(
      (x) => String(x?.label || "").trim().toLowerCase() === raw.toLowerCase(),
    );

    if (match?.shortName) return String(match.shortName).trim();
    if (match?.abbr) return String(match.abbr).trim();
    if (match?.code) return String(match.code).trim();

    const words = raw.split(/\s+/).filter(Boolean);
    if (words.length >= 2) {
      return words.map((w) => w[0]).join("").toUpperCase();
    }

    return raw.slice(0, 2).toUpperCase();
  };

  const cleanCellText = (text) => {
    let result = String(text || "");

    result = result.replace(/\bworking\s*:\s*/gi, "");
    result = result.replace(/devider_line/gi, "");

    (leaveOptionsCache || []).forEach((opt) => {
      const label = String(opt?.label || "").trim();
      if (!label) return;

      const abbr = getLeaveAbbreviation(label);
      if (!abbr) return;

      const escaped = label.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
      result = result.replace(new RegExp(escaped, "gi"), abbr);
    });

    Object.keys(LEAVE_ABBR).forEach((name) => {
      const abbr = LEAVE_ABBR[name];
      const escaped = name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
      result = result.replace(new RegExp(escaped, "gi"), abbr);
    });

    result = result.replace(/[ \t]+\n/g, "\n");
    result = result.replace(/\n[ \t]+/g, "\n");
    result = result.replace(/[ \t]{2,}/g, " ");
    result = result.replace(/\n{3,}/g, "\n\n");

    return result.trim();
  };

  const formatAttendanceCell = (cell) => {
    const txt = String(cell?.time || "").trim().replace(/DIVIDER_LINE/gi, "");
    if (txt) return cleanCellText(txt).replace(/\n/g, " | ");
    return cell?.status != null ? String(cell.status) : "—";
  };

  const WORKING_PREFIXES = ["working", "dawam", "دوام"];
  const formatLeaveOnlyCell = (cell) => {
    const txt = String(cell?.time || "").trim().replace(/DIVIDER_LINE/gi, "");
    if (!txt) return "—";
    const leaveLines = txt
      .split("\n")
      .map((l) => l.trim())
      .filter((l) => {
        if (!l || l.toLowerCase() === "divider_line") return false;
        if (!l.includes(":")) return false;
        const head = l.split(":")[0].trim().toLowerCase();
        return !WORKING_PREFIXES.some((p) => head === p);
      });
    if (!leaveLines.length) return "—";
    return leaveLines.map((l) => cleanCellText(l)).join(" | ");
  };

  const toMinutes = (t) => {
    const [h, m] = String(t || "0:0").split(":").map(Number);
    return (isNaN(h) ? 0 : h) * 60 + (isNaN(m) ? 0 : m);
  };

  const parseLeaveEntries = (cellTxt) => {
    const txt = String(cellTxt || "").trim().replace(/DIVIDER_LINE/gi, "");
    if (!txt) return [];
    return txt
      .split("\n")
      .map((l) => l.trim())
      .filter((l) => {
        if (!l || l.toLowerCase() === "divider_line") return false;
        if (!l.includes(":")) return false;
        const head = l.split(":")[0].trim().toLowerCase();
        return !WORKING_PREFIXES.some((p) => head === p);
      })
      .map((line) => {
        const colonIdx = line.indexOf(":");
        const rawHead = line.substring(0, colonIdx).trim();
        const abbr = cleanCellText(rawHead) || rawHead;
        const rest = line.substring(colonIdx + 1).trim();
        const match = rest.match(/(\d{1,2}:\d{2})\s*[~-]\s*(\d{1,2}:\d{2})/);
        if (!match) return null;
        return { abbr, time_in: match[1], time_out: match[2] };
      })
      .filter(Boolean);
  };

  const getColumnValue = (columnKey, emp, iso) => {
    const dayObj = emp?.days?.[iso] || {};

    if (columnKey === "attendance") {
      return reportSettings?.leave
        ? formatLeaveOnlyCell(dayObj)
        : formatAttendanceCell(dayObj);
    }

    if (columnKey === "realSchedule") {
      return normalizeSch(dayObj?.sumtimesh ?? "") || "—";
    }

    if (columnKey === "supposedSchedule") {
      const dayKey = getDayKeyFromIso(iso);
      return dayObj?.rulestime?.[dayKey] ?? "—";
    }

    if (columnKey === "workingHours") {
      const h = getCellHours(dayObj);
      return h > 0 ? `${h.toFixed(2)} h` : "—";
    }

    if (columnKey === "additionRetentionHours") {
      const dayKey = getDayKeyFromIso(iso);
      const realTxt = normalizeSch(dayObj?.sumtimesh ?? "");
      const rulesTxt = dayObj?.rulestime?.[dayKey] ?? "—";

      const finalMin = scheduleMinutes(realTxt);
      const rulesMin = scheduleMinutes(rulesTxt);
      const diffMin =
        finalMin == null || rulesMin == null ? null : finalMin - rulesMin;

      return formatSignedHours(diffMin);
    }

    if (columnKey === "additionRetentionAfterRules") {
      const mins = Number(dayObj?.scheduleAfterRulesMinutes ?? 0) || 0;
      return mins ? `${(mins / 60).toFixed(2)} h` : "—";
    }

    if (columnKey === "workingHoursAfterRules") {
      const cellHours = getCellHours(dayObj);
      const cellMinutes = Math.round(Number(cellHours || 0) * 60);

      const dayKey = getDayKeyFromIso(iso);
      const realTxt = normalizeSch(dayObj?.sumtimesh ?? "");
      const rulesTxt = dayObj?.rulestime?.[dayKey] ?? "—";

      const finalMin = scheduleMinutes(realTxt);
      const rulesMin = scheduleMinutes(rulesTxt);
      const additionRetentionMinutes =
        finalMin == null || rulesMin == null ? 0 : finalMin - rulesMin;

      const scheduleAfterRulesMinutes =
        Number(dayObj?.scheduleAfterRulesMinutes ?? 0) || 0;

      const result =
        cellMinutes - additionRetentionMinutes + scheduleAfterRulesMinutes;

      return result ? `${(result / 60).toFixed(2)} h` : "—";
    }

    return "—";
  };

  const getColumnTotal = (columnKey, emp) => {
    if (columnKey === "attendance") {
      return `${getTotalHours(emp, visibleDays).toFixed(2)} h`;
    }

    if (columnKey === "realSchedule") {
      const total = visibleDays.reduce((acc, iso) => {
        const txt = normalizeSch(emp?.days?.[iso]?.sumtimesh ?? "");
        const min = scheduleMinutes(txt);
        return acc + (min ?? 0);
      }, 0);
      return formatSignedHours(total);
    }

    if (columnKey === "supposedSchedule") {
      const total = visibleDays.reduce((acc, iso) => {
        const dayKey = getDayKeyFromIso(iso);
        const txt = emp?.days?.[iso]?.rulestime?.[dayKey] ?? "—";
        const min = scheduleMinutes(txt);
        return acc + (min ?? 0);
      }, 0);
      return formatSignedHours(total);
    }

    if (columnKey === "workingHours") {
      return `${getTotalHours(emp, visibleDays).toFixed(2)} h`;
    }

    if (columnKey === "additionRetentionHours") {
      const total = visibleDays.reduce((acc, iso) => {
        const dayKey = getDayKeyFromIso(iso);
        const dayObj = emp?.days?.[iso] || {};
        const realTxt = normalizeSch(dayObj?.sumtimesh ?? "");
        const rulesTxt = dayObj?.rulestime?.[dayKey] ?? "—";
        const finalMin = scheduleMinutes(realTxt);
        const rulesMin = scheduleMinutes(rulesTxt);
        const diffMin =
          finalMin == null || rulesMin == null ? 0 : finalMin - rulesMin;
        return acc + diffMin;
      }, 0);

      return formatSignedHours(total);
    }

    if (columnKey === "additionRetentionAfterRules") {
      const total = visibleDays.reduce((acc, iso) => {
        return acc + (Number(emp?.days?.[iso]?.scheduleAfterRulesMinutes ?? 0) || 0);
      }, 0);

      return total ? `${(total / 60).toFixed(2)} h` : "—";
    }

    if (columnKey === "workingHoursAfterRules") {
      const total = visibleDays.reduce((acc, iso) => {
        const dayObj = emp?.days?.[iso] || {};

        const cellHours = getCellHours(dayObj);
        const cellMinutes = Math.round(Number(cellHours || 0) * 60);

        const dayKey = getDayKeyFromIso(iso);
        const realTxt = normalizeSch(dayObj?.sumtimesh ?? "");
        const rulesTxt = dayObj?.rulestime?.[dayKey] ?? "—";

        const finalMin = scheduleMinutes(realTxt);
        const rulesMin = scheduleMinutes(rulesTxt);
        const additionRetentionMinutes =
          finalMin == null || rulesMin == null ? 0 : finalMin - rulesMin;

        const scheduleAfterRulesMinutes =
          Number(dayObj?.scheduleAfterRulesMinutes ?? 0) || 0;

        return (
          acc +
          (cellMinutes - additionRetentionMinutes + scheduleAfterRulesMinutes)
        );
      }, 0);

      return total ? `${(total / 60).toFixed(2)} h` : "—";
    }

    return "—";
  };

  const printKeys = reportSettings?.leave ? displayedKeys : filteredKeys;
  const employeesHtml = printKeys
    .map((empKey) => {
      const emp = monthData?.[empKey];
      const empName = emp?.employee || empKey;

      const headerColsHtml = columns
        .map((col) => `<th>${escapeHtml(col.label)}</th>`)
        .join("");

      const dayRowsHtml = visibleDays
        .map((iso) => {
          const dayLabel = dayjs(iso).format("ddd DD/MM/YYYY");

          const valuesHtml = columns
            .map(
              (col) =>
                `<td>${escapeHtml(getColumnValue(col.key, emp, iso))}</td>`,
            )
            .join("");

          return `
            <tr>
              <td class="day-cell">${escapeHtml(dayLabel)}</td>
              ${valuesHtml}
            </tr>
          `;
        })
        .join("");

      const totalsHtml = columns
        .map(
          (col) =>
            `<td class="total-cell">${escapeHtml(getColumnTotal(col.key, emp))}</td>`,
        )
        .join("");

      const leaveTypeSummaryHtml = (() => {
        if (!reportSettings?.leave) return "";
        const leaveHours = {};
        visibleDays.forEach((iso) => {
          const dayObj = emp?.days?.[iso] || {};
          parseLeaveEntries(dayObj?.time).forEach(({ abbr, time_in, time_out }) => {
            const hrs = (toMinutes(time_out) - toMinutes(time_in)) / 60;
            if (hrs > 0) leaveHours[abbr] = (leaveHours[abbr] || 0) + hrs;
          });
        });
        const entries = Object.entries(leaveHours);
        if (!entries.length) return "";
        const text = entries
          .map(([abbr, hrs]) => `${escapeHtml(abbr)}: ${hrs.toFixed(2)}h`)
          .join(" &nbsp;|&nbsp; ");
        return `<div class="leave-type-summary"><strong>Leave Totals:</strong> ${text}</div>`;
      })();

      return `
        <section class="employee-section">
          <div class="employee-title">${escapeHtml(empName)}</div>
          <table>
            <thead>
              <tr>
                <th class="day-head">Day</th>
                ${headerColsHtml}
              </tr>
            </thead>
            <tbody>
              ${dayRowsHtml}
              <tr class="totals-row">
                <td class="day-cell total-label">Totals</td>
                ${totalsHtml}
              </tr>
            </tbody>
          </table>
          ${leaveTypeSummaryHtml}
        </section>
      `;
    })
    .join("");

  const printWindow = window.open("", "_blank", "width=1200,height=900");
  if (!printWindow) return;

  printWindow.document.write(`
    <html>
      <head>
        <title>Attendance Grid</title>
        <style>
          @page {
            size: portrait;
            margin: 10mm;
          }

          * {
            box-sizing: border-box;
          }

          body {
            font-family: Arial, Helvetica, sans-serif;
            color: #111;
            margin: 0;
            padding: 0;
          }

          .employee-section {
            page-break-after: always;
            break-after: page;
            margin-bottom: 18px;
          }

          .employee-section:last-child {
            page-break-after: auto;
            break-after: auto;
          }

          .employee-title {
            font-size: 20px;
            font-weight: 700;
            margin-bottom: 12px;
            padding: 10px 12px;
            border: 1px solid #333;
            background: #f3f3f3;
          }

          table {
            width: 100%;
            border-collapse: collapse;
            table-layout: fixed;
          }

          th,
          td {
            border: 1px solid #333;
            padding: 6px 5px;
            font-size: 11px;
            text-align: center;
            vertical-align: middle;
            word-wrap: break-word;
            white-space: pre-wrap;
          }

          th {
            background: #eaeaea;
            font-weight: 700;
          }

          .day-head,
          .day-cell {
            width: 140px;
            font-weight: 700;
            background: #f7f7f7;
          }

          .total-cell,
          .total-label {
            font-weight: 700;
            background: #fafafa;
          }

          .totals-row td {
            font-weight: 700;
          }

          .leave-type-summary {
            margin-top: 6px;
            padding: 6px 10px;
            font-size: 12px;
            border: 1px solid #bbb;
            background: #f9f9f9;
          }
        </style>
      </head>
      <body>
        ${employeesHtml}
      </body>
    </html>
  `);

  printWindow.document.close();
  printWindow.focus();
  printWindow.print();
  printWindow.close();
}, [
  reportSettings,
  hideRulesInfos,
  filteredKeys,
  displayedKeys,
  monthData,
  visibleDays,
  leaveOptionsCache,
]);

const handleClickSummary = useCallback(() => {
  const visibleFieldKeys = (() => {
    if (!reportSettings || reportSettings.all) {
      return new Set([
        "attendance",
        "realSchedule",
        "supposedSchedule",
        "workingHours",
        "additionRetentionHours",
        "additionRetentionAfterRules",
        "workingHoursAfterRules",
      ]);
    }

    if (reportSettings.attendance) {
      return new Set([
        "attendance",
        "realSchedule",
        "supposedSchedule",
        "workingHours",
        "additionRetentionHours",
        "additionRetentionAfterRules",
        "workingHoursAfterRules",
      ]);
    }

    if (reportSettings.custom) {
      return new Set(reportSettings.customFields || []);
    }

    return new Set([
      "attendance",
      "realSchedule",
      "supposedSchedule",
      "workingHours",
      "additionRetentionHours",
      "additionRetentionAfterRules",
      "workingHoursAfterRules",
    ]);
  })();

  const columns = [
    visibleFieldKeys.has("attendance") && {
      key: "attendance",
      label: "Attendance",
    },
    !hideRulesInfos &&
      visibleFieldKeys.has("realSchedule") && {
        key: "realSchedule",
        label: "Real Schedule",
      },
    !hideRulesInfos &&
      visibleFieldKeys.has("supposedSchedule") && {
        key: "supposedSchedule",
        label: "Supposed Schedule",
      },
    !hideRulesInfos &&
      visibleFieldKeys.has("workingHours") && {
        key: "workingHours",
        label: "Hours",
      },
    !hideRulesInfos &&
      visibleFieldKeys.has("additionRetentionHours") && {
        key: "additionRetentionHours",
        label: "Addition/Retention Hours",
      },
    !hideRulesInfos &&
      visibleFieldKeys.has("additionRetentionAfterRules") && {
        key: "additionRetentionAfterRules",
        label: "Addition / Retention After Rules",
      },
    !hideRulesInfos &&
      visibleFieldKeys.has("workingHoursAfterRules") && {
        key: "workingHoursAfterRules",
        label: "Hours After Rules",
      },
  ].filter(Boolean);

  const escapeHtml = (value) =>
    String(value ?? "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;");

  const getColumnTotal = (columnKey, emp) => {
    if (columnKey === "attendance") {
      return `${getTotalHours(emp, visibleDays).toFixed(2)} h`;
    }

    if (columnKey === "realSchedule") {
      const total = visibleDays.reduce((acc, iso) => {
        const txt = normalizeSch(emp?.days?.[iso]?.sumtimesh ?? "");
        const min = scheduleMinutes(txt);
        return acc + (min ?? 0);
      }, 0);
      return formatSignedHours(total);
    }

    if (columnKey === "supposedSchedule") {
      const total = visibleDays.reduce((acc, iso) => {
        const dayKey = getDayKeyFromIso(iso);
        const txt = emp?.days?.[iso]?.rulestime?.[dayKey] ?? "—";
        const min = scheduleMinutes(txt);
        return acc + (min ?? 0);
      }, 0);
      return formatSignedHours(total);
    }

    if (columnKey === "workingHours") {
      return `${getTotalHours(emp, visibleDays).toFixed(2)} h`;
    }

    if (columnKey === "additionRetentionHours") {
      const total = visibleDays.reduce((acc, iso) => {
        const dayKey = getDayKeyFromIso(iso);
        const dayObj = emp?.days?.[iso] || {};
        const realTxt = normalizeSch(dayObj?.sumtimesh ?? "");
        const rulesTxt = dayObj?.rulestime?.[dayKey] ?? "—";
        const finalMin = scheduleMinutes(realTxt);
        const rulesMin = scheduleMinutes(rulesTxt);
        const diffMin =
          finalMin == null || rulesMin == null ? 0 : finalMin - rulesMin;
        return acc + diffMin;
      }, 0);

      return formatSignedHours(total);
    }

    if (columnKey === "additionRetentionAfterRules") {
      const total = visibleDays.reduce((acc, iso) => {
        return acc + (Number(emp?.days?.[iso]?.scheduleAfterRulesMinutes ?? 0) || 0);
      }, 0);

      return total ? `${(total / 60).toFixed(2)} h` : "—";
    }

    if (columnKey === "workingHoursAfterRules") {
      const total = visibleDays.reduce((acc, iso) => {
        const dayObj = emp?.days?.[iso] || {};

        const cellHours = getCellHours(dayObj);
        const cellMinutes = Math.round(Number(cellHours || 0) * 60);

        const dayKey = getDayKeyFromIso(iso);
        const realTxt = normalizeSch(dayObj?.sumtimesh ?? "");
        const rulesTxt = dayObj?.rulestime?.[dayKey] ?? "—";

        const finalMin = scheduleMinutes(realTxt);
        const rulesMin = scheduleMinutes(rulesTxt);
        const additionRetentionMinutes =
          finalMin == null || rulesMin == null ? 0 : finalMin - rulesMin;

        const scheduleAfterRulesMinutes =
          Number(dayObj?.scheduleAfterRulesMinutes ?? 0) || 0;

        return (
          acc +
          (cellMinutes - additionRetentionMinutes + scheduleAfterRulesMinutes)
        );
      }, 0);

      return total ? `${(total / 60).toFixed(2)} h` : "—";
    }

    return "—";
  };

  const headHtml = columns
    .map((col) => `<th>${escapeHtml(col.label)}</th>`)
    .join("");

  const bodyHtml = filteredKeys
    .map((empKey) => {
      const emp = monthData?.[empKey];
      const empName = emp?.employee || empKey;

      const valuesHtml = columns
        .map((col) => `<td>${escapeHtml(getColumnTotal(col.key, emp))}</td>`)
        .join("");

      return `
        <tr>
          <td class="emp-cell">${escapeHtml(empName)}</td>
          ${valuesHtml}
        </tr>
      `;
    })
    .join("");

  const printWindow = window.open("", "_blank", "width=1100,height=900");
  if (!printWindow) return;

  printWindow.document.write(`
    <html>
      <head>
        <title>Attendance Summary</title>
        <style>
          @page {
            size: portrait;
            margin: 10mm;
          }

          * {
            box-sizing: border-box;
          }

          body {
            font-family: Arial, Helvetica, sans-serif;
            color: #111;
            margin: 0;
            padding: 0;
          }

          .title {
            font-size: 22px;
            font-weight: 800;
            margin-bottom: 6px;
          }

          .sub-title {
            font-size: 12px;
            color: #555;
            margin-bottom: 14px;
          }

          table {
            width: 100%;
            border-collapse: collapse;
            table-layout: fixed;
          }

          th,
          td {
            border: 1px solid #333;
            padding: 7px 6px;
            font-size: 11px;
            text-align: center;
            vertical-align: middle;
            word-wrap: break-word;
            white-space: pre-wrap;
          }

          th {
            background: #eaeaea;
            font-weight: 700;
          }

          .emp-head,
          .emp-cell {
            width: 180px;
            background: #f7f7f7;
            font-weight: 700;
          }

          tbody tr:nth-child(even) td {
            background: #fcfcfc;
          }
        </style>
      </head>
      <body>
        <div class="title">Attendance Summary Report</div>
        <div class="sub-title">
          From ${escapeHtml(safeRange.start.format("DD/MM/YYYY"))}
          to ${escapeHtml(safeRange.end.format("DD/MM/YYYY"))}
        </div>

        <table>
          <thead>
            <tr>
              <th class="emp-head">Employee</th>
              ${headHtml}
            </tr>
          </thead>
          <tbody>
            ${bodyHtml}
          </tbody>
        </table>
      </body>
    </html>
  `);

  printWindow.document.close();
  printWindow.focus();
  printWindow.print();
  printWindow.close();
}, [
  reportSettings,
  hideRulesInfos,
  filteredKeys,
  monthData,
  visibleDays,
  safeRange,
]);

//check ReportType
const handlePrintClick = useCallback(() => {
  if (reportSettings?.reportType === "summary") {
    handleClickSummary();
    return;
  }

  handlePrintGrid();
}, [reportSettings, handleClickSummary, handlePrintGrid]);
  return (
    <Box sx={{ p: 2 }}>
      {/* <Paper
        elevation={0}
        sx={{
          p: 2,
          borderRadius: 3,
          border: isDark ? "1px solid rgba(255,255,255,.08)" : "1px solid rgba(0,0,0,.08)",
          overflow: "hidden",
          bgcolor: isDark ? "#1e1e1e" : "#ffffff",
        }}
      > */}
      <Paper
        elevation={0}
        sx={{
          p: 2,
          borderRadius: 3,
          border: "1px solid rgba(0,0,0,.08)",
          overflow: "hidden",
        }}
      >
        <Stack spacing={2}>
          {!hidePanels && (
            <>
              <Stack
                direction={{ xs: "column", md: "row" }}
                alignItems={{ xs: "flex-start", md: "center" }}
                justifyContent="space-between"
                spacing={2}
              >
                <Box>
                  <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant="h5" fontWeight={800}>
                    Attendance
                  </Typography>
                  <Chip
                    label={`Org: ${orgName || "not set"}`}
                    size="small"
                    sx={{
                      bgcolor: orgName ? "#e8f5e9" : "#ffebee",
                      color: orgName ? "#2e7d32" : "#c62828",
                      fontWeight: 700,
                    }}
                  />
                  </Stack>
                  <Typography variant="body2" color="text.secondary">
                    Employees: {totalEmployees} | Showing: {displayedKeys.length}
                  </Typography>
                </Box>

                <Stack direction="row" spacing={1} flexWrap="wrap">
                  <Button
                    variant="contained"
                    startIcon={<FunctionsIcon />}
                    onClick={handleCalculateRules}
                  >
                    Calculate Rules
                  </Button>

                  <Button
                    variant="outlined"
                    startIcon={<PrintIcon />}
                    onClick={handlePrintClick}
                  >
                    Print
                  </Button>

                  <ReportSettingsLauncher
                    gridFields={reportGridFields}
                    initialSettings={reportSettings}
                    onApply={(settings) => {
                      setReportSettings(settings);
                      localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
                    }}
                  />
                </Stack>
              </Stack>

              <DateFilterBar
                fromDate={fromDate}
                toDate={toDate}
                setFromDate={setFromDate}
                setToDate={setToDate}
                allMonths={allMonths}
                setAllMonths={setAllMonths}
                appliedFromDate={appliedFromDate}
                appliedToDate={appliedToDate}
                appliedAllMonths={appliedAllMonths}
                onApply={handleApplyDates}
                fullMonth={fullMonth}
                setFullMonth={setFullMonth}
              />

              <FiltersToolbar
                employeeOptions={employeeOptions}
                selectedEmps={selectedEmps}
                onEmployeesChange={setSelectedEmps}
                leaveOptions={leaveOptionsCache}
                selectedLeaves={selectedLeaves}
                onLeavesChange={setSelectedLeaves}
                timingFilterOptions={timingFilterOptions}
                selectedTimingFilter={selectedTimingFilter}
                onTimingFilterChange={setSelectedTimingFilter}
              />

              <Stack direction="row" spacing={1.5} alignItems="center">
                <Autocomplete
                  multiple
                  size="small"
                  options={machineOptions}
                  value={selectedMachines}
                  onChange={(_, newVal) => setSelectedMachines(newVal)}
                  disableCloseOnSelect
                  filterSelectedOptions
                  getOptionLabel={(opt) => opt.label}
                  isOptionEqualToValue={(opt, val) =>
                    String(opt.key) === String(val.key)
                  }
                  sx={{ flex: 1, minWidth: 280 }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Fingerprint machines"
                      placeholder="Select machines..."
                      sx={{ backgroundColor: "white", borderRadius: 2 }}
                    />
                  )}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        {...getTagProps({ index })}
                        key={option.key}
                        label={option.label}
                        size="small"
                        sx={{
                          fontWeight: 700,
                          bgcolor: "rgba(0,121,107,.08)",
                          color: "#004d40",
                        }}
                      />
                    ))
                  }
                />

                <Button
                  variant="contained"
                  onClick={handleGetAndSync}
                  disabled={machineActionLoading || locationSyncLoading}
                  startIcon={
                    machineActionLoading || locationSyncLoading ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : (
                      <FingerprintIcon />
                    )
                  }
                  sx={{
                    fontWeight: 800,
                    borderRadius: 2,
                    textTransform: "none",
                    boxShadow: "0 8px 18px rgba(0,0,0,.12)",
                    bgcolor: "#ffffff",
                    // color: "#00796b",
                    color:  "#1976d2",
                    border: "1px solid #e4e7e7",
                    "&:hover": { bgcolor: "#f7f7f7" },
                  }}
                >
                  APPLY 
                </Button>

                {/* {machineStatusMsg && (
                <Typography
                  variant="caption"
                  sx={{
                    ml: 1,
                    color: machineStatusMsg.startsWith("⚠") ? "error.main" : "text.secondary",
                    fontWeight: machineStatusMsg.startsWith("⚠") ? 700 : 400,
                  }}
                >
                  {machineStatusMsg}
                </Typography>
              )} */}

                <Button
                  variant="outlined"
                  startIcon={<SyncIcon />}
                  onClick={async () => {
                    try {
                      const rawBase = localStorage.getItem("saved_sett_api_url");
                      const API_BASE_URL = normalizeBaseUrl(rawBase);
                      if (!API_BASE_URL) return;
                      const from = appliedFromDate.format("YYYY-MM-DD");
                      const to = appliedToDate.format("YYYY-MM-DD");

                      await Promise.all([
                        axios.get(`${API_BASE_URL}/api/schedule/sync`, {
                          params: { fromdate: from, todate: to },
                          withCredentials: true,
                        }),
                        // sync pleave for approve leave count and local
                        axios.get(`${API_BASE_URL}/api/pleave/sync`, {
                          params: { fromdate: from, todate: to },
                          withCredentials: true,
                        }),
                      ]);

                      setToast({ open: true, msg: "All changes saved.", sev: "success" });
                      handleApplyDates(appliedFromDate, appliedToDate);
                    } catch (e) {
                      console.error("Save All sync failed:", e);
                      setToast({ open: true, msg: "Sync failed.", sev: "error" });
                    }
                  }}
                  sx={{
                    fontWeight: 800,
                    borderRadius: 2,
                    textTransform: "none",
                    boxShadow: "0 8px 18px rgba(0,0,0,.12)",
                    bgcolor: "#ffffff",
                    color: "#00796b",
                    border: "1px solid #e4e7e7",
                    "&:hover": { bgcolor: "#f7f7f7" },
                  }}
                >
                  Save All
                </Button>
                
                {/* {showAttendanceDropdown && ( */}
                  {/* <Button
                  variant="outlined"
                  startIcon={<AssignmentIcon />}
                  onClick={() => navigate("/home/calendar/visits?hr=true")}
                  sx={{
                    textTransform: "none",
                    fontWeight: 700,
                    borderRadius: 3,
                    borderColor: "#00796b",
                    color: "#004d40",
                    "&:hover": { bgcolor: "#e0f2f1", borderColor: "#004d40" },
                  }}
                  >
                    Visit Report
                  </Button> */}
                {/* )} */}
                <Button
                  variant="outlined"
                  color="success"
                  startIcon={<DownloadIcon />}
                  onClick={handleExportTimings}
                  disabled={loading}
                  sx={{
                    textTransform: "none",
                    fontWeight: 800,
                    borderRadius: 3,
                    px: 2.5,
                    py: 1.15,
                    minHeight: 46,
                    boxShadow: "0 10px 24px rgba(25,118,210,0.18)",
                    background: "linear-gradient(135deg, #7b1fa2 0%, #1976d2 100%)",
                    color: "#ffffff",
                  }}
                >
                  Export to Excel
                </Button>
                
                
              </Stack>

              <Stack
                direction="row"
                spacing={2}
                flexWrap="wrap"
                alignItems="center"
                justifyContent="space-between"
              >
                <Stack direction="row" spacing={2} flexWrap="wrap">
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={hideWeekends}
                        onChange={(e) => setHideWeekends(e.target.checked)}
                      />
                    }
                    label="Hide weekends"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={hideRulesInfos}
                        onChange={(e) => setHideRulesInfos(e.target.checked)}
                      />
                    }
                    label="Hide rules info rows"
                  />
                </Stack>

          {/* legend color */}
                <Stack direction="row" spacing={1} alignItems="center">
                  <Tooltip title="Color legend">
                    <IconButton
                      size="small"
                      onClick={(e) => setLegendAnchor(e.currentTarget)}
                      sx={{
                        border: "1px solid #e4e7e7",
                        borderRadius: 2,
                        bgcolor: "#ffffff",
                        "&:hover": { bgcolor: "#f7f7f7" },
                      }}
                    >
                      <InfoOutlinedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>

                <IconButton
                  onClick={toggleHidePanels}
                  title="Collapse filters"
                  sx={{
                    border: "1px solid #e4e7e7",
                    borderRadius: 2,
                    bgcolor: "#ffffff",
                    color:  "#1976d2",
                    "&:hover": { bgcolor: "#f7f7f7" },
                  }}
                  
                >
                  <RemoveIcon />
                </IconButton>
                </Stack>
              </Stack>
            </>
          )}

          {hidePanels && (
            <Stack direction="row" justifyContent="flex-end" spacing={1}>
              <Tooltip title="Color legend">
                <IconButton
                  size="small"
                  onClick={(e) => setLegendAnchor(e.currentTarget)}
                  sx={{
                    border: "1px solid #e4e7e7",
                    borderRadius: 2,
                    bgcolor: "#ffffff",
                    "&:hover": { bgcolor: "#f7f7f7" },
                  }}
                >
                  <InfoOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>

              <IconButton
                onClick={toggleHidePanels}
                title="Show filters"
                sx={{
                  border: "1px solid #e4e7e7",
                  borderRadius: 2,
                  bgcolor: "#ffffff",
                  color: "#1976d2",
                  "&:hover": { bgcolor: "#f7f7f7" },
                }}
              >
                <AddIcon />
              </IconButton>
            </Stack>
          )}

          <Popover
            open={legendOpen}
            anchorEl={legendAnchor}
            onClose={() => setLegendAnchor(null)}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
            PaperProps={{ sx: { borderRadius: 2, p: 1.5, boxShadow: 4 } }}
          >
            <Box sx={{ minWidth: 220 }}>
              <Typography variant="subtitle2" fontWeight={800} sx={{ mb: 1 }}>
                Time color legend
              </Typography>
              <Stack spacing={0.9}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Box sx={{ width: 12, height: 12, borderRadius: "50%", bgcolor: "#b71c1c" }} />
                  <Typography fontSize={13}>Phone (GPS) entry</Typography>
                </Stack>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Box sx={{ width: 12, height: 12, borderRadius: "50%", bgcolor: "#0d47a1" }} />
                  <Typography fontSize={13}>HR manual entry</Typography>
                </Stack>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Box sx={{ width: 12, height: 12, borderRadius: "50%", bgcolor: "#2e7d32" }} />
                  <Typography fontSize={13}>Employee manual entry</Typography>
                </Stack>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Box sx={{ width: 12, height: 12, borderRadius: "50%", bgcolor: "#e65100" }} />
                  <Typography fontSize={13}>Machine (fingerprint) entry</Typography>
                </Stack>
              </Stack>
            </Box>
          </Popover>

          {(loading || machineLoading) && (
            <Stack direction="row" spacing={1} alignItems="center">
              <CircularProgress size={22} />
              <Typography variant="body2">Loading...</Typography>
            </Stack>
          )}

          <Box ref={printAreaRef}>
            <AttendanceTable
              refContainer={containerRef}
              monthData={monthData}
              filteredKeys={displayedKeys}
              visibleDays={visibleDays}
              hideRulesInfos={hideRulesInfos}
              selectedLeaves={selectedLeaves}
              selectedTimingFilter={selectedTimingFilter}
              leaveOptionsCache={leaveOptionsCache}
              restLeavesByEmp={restLeavesByEmp}
              onCellDoubleClick={openEditDialog}
              reportSettings={reportSettings}
            />
          </Box>
        </Stack>
        {/* <Stack spacing={2}>
          <Stack
            direction={{ xs: "column", md: "row" }}
            alignItems={{ xs: "flex-start", md: "center" }}
            justifyContent="space-between"
            spacing={2}
          >
            <Box>
              <Typography variant="h5" fontWeight={800}>
                Attendance
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Employees: {totalEmployees} | Showing: {displayedKeys.length}
              </Typography>
            </Box>

            <Stack direction="row" spacing={1} flexWrap="wrap">
              <Button
                variant="contained"
                startIcon={<FunctionsIcon />}
                onClick={handleCalculateRules}
              >
                Calculate Rules
              </Button>

              

              <Button
                variant="outlined"
                startIcon={<PrintIcon />}
                onClick={handlePrintClick}
              >
                Print
              </Button>
              

              

              <ReportSettingsLauncher
                gridFields={reportGridFields}
                initialSettings={reportSettings}
                onApply={(settings) => {
                  setReportSettings(settings);
                  localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
                }}
              />
            </Stack>
          </Stack>

          <DateFilterBar
            fromDate={fromDate}
            toDate={toDate}
            setFromDate={setFromDate}
            setToDate={setToDate}
            allMonths={allMonths}
            setAllMonths={setAllMonths}
            appliedFromDate={appliedFromDate}
            appliedToDate={appliedToDate}
            appliedAllMonths={appliedAllMonths}
            onApply={handleApplyDates}  // when the apply is clicked 
            fullMonth={fullMonth}  
            setFullMonth={setFullMonth}
          />

          <FiltersToolbar
            employeeOptions={employeeOptions}
            selectedEmps={selectedEmps}
            onEmployeesChange={setSelectedEmps}
            leaveOptions={leaveOptionsCache}
            selectedLeaves={selectedLeaves}
            onLeavesChange={setSelectedLeaves}
            timingFilterOptions={timingFilterOptions}
            selectedTimingFilter={selectedTimingFilter}
            onTimingFilterChange={setSelectedTimingFilter}
          />

          <Stack direction="row" spacing={1.5} alignItems="center">
            <Autocomplete
              multiple
              size="small"
              options={machineOptions}
              value={selectedMachines}
              onChange={(_, newVal) => setSelectedMachines(newVal)}
              disableCloseOnSelect
              filterSelectedOptions
              getOptionLabel={(opt) => opt.label}
              isOptionEqualToValue={(opt, val) =>
                String(opt.key) === String(val.key)
              }
              sx={{ flex: 1, minWidth: 280 }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Fingerprint machines"
                  placeholder="Select machines..."
                  sx={{ backgroundColor: "white", borderRadius: 2 }}
                />
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    {...getTagProps({ index })}
                    key={option.key}
                    label={option.label}
                    size="small"
                    sx={{
                      fontWeight: 700,
                      bgcolor: "rgba(0,121,107,.08)",
                      color: "#004d40",
                    }}
                  />
                ))
              }
            />
           {/* <Button
              variant="contained"
              onClick={handleGetMachine}
              disabled={machineActionLoading}
              //startIcon={!machineActionLoading ? <SyncIcon /> : null}
              startIcon={!machineActionLoading }

              sx={{
                fontWeight: 800,
                borderRadius: 2,
                textTransform: "none",
                boxShadow: "0 8px 18px rgba(0,0,0,.12)",
                bgcolor: "#ffffff",
                color: "#00796b",
                border: "1px solid #e4e7e7",
                "&:hover": { bgcolor: "#f7f7f7" },
                minWidth: 52,
              }}
            >
              <FingerprintIcon />
            </Button>

            <IconButton
              onClick={handleLocationSync}
              disabled={locationSyncLoading}
              sx={{
                border: "1px solid #e4e7e7",
                borderRadius: 2,
                bgcolor: "#ffffff",
                color: "#00796b",
                boxShadow: "0 8px 18px rgba(0,0,0,.12)",
                "&:hover": { bgcolor: "#f7f7f7" },
              }}
            >
              <SyncIcon />
            </IconButton> */}
            {/* <Button
            variant="contained"
            onClick={handleGetAndSync}
            disabled={machineActionLoading || locationSyncLoading}
            startIcon={
              machineActionLoading || locationSyncLoading
              ? <CircularProgress size={16} color="inherit" />
              : <FingerprintIcon />
            }
            sx={{
              fontWeight: 800,
              borderRadius: 2,
              textTransform: "none",
              boxShadow: "0 8px 18px rgba(0,0,0,.12)",
              bgcolor: "#ffffff",
              color: "#00796b",
              border: "1px solid #e4e7e7",
              "&:hover": { bgcolor: "#f7f7f7" },
            }}
            >
              Get Attendance
            </Button>

          {/* New Button save!!  */}
            {/* <Button
              variant="outlined"
              startIcon={<SyncIcon />}
              onClick={async () => {
                try {
                  const rawBase = localStorage.getItem("saved_sett_api_url");
                  const API_BASE_URL = normalizeBaseUrl(rawBase);
                  if (!API_BASE_URL) return;
                  const from = appliedFromDate.format("YYYY-MM-DD");
                  const to   = appliedToDate.format("YYYY-MM-DD");
                  await axios.get(`${API_BASE_URL}/api/schedule/sync`, {
                    params: { fromdate: from, todate: to },
                    withCredentials: true,
                  });
                  setToast({ open: true, msg: "All changes saved.", sev: "success" });
                  handleApplyDates(appliedFromDate, appliedToDate);
                } catch (e) {
                  console.error("Save All sync failed:", e);
                  setToast({ open: true, msg: "Sync failed.", sev: "error" });
                }
              }}
              sx={{ 
                fontWeight: 800,
                borderRadius: 2,
                textTransform: "none",
                boxShadow: "0 8px 18px rgba(0,0,0,.12)",
                bgcolor: "#ffffff",
                color: "#00796b",
                border: "1px solid #e4e7e7",
                "&:hover": { bgcolor: "#f7f7f7" },
              }}
            >
              Save All
            </Button>

            <Button
              variant="outlined"
              color="success"
              startIcon={<DownloadIcon />}
              onClick={handleExportTimings}
              disabled={loading}
              sx={{
              textTransform: "none",
              fontWeight: 800,
              borderRadius: 3,
              px: 2.5,
              py: 1.15,
              minHeight: 46,
              boxShadow: "0 10px 24px rgba(25,118,210,0.18)",
              background: "linear-gradient(135deg, #7b1fa2 0%, #1976d2 100%)",
              color: "#ffffff",
              
            }}
            >
              Export to Excel
            </Button>
            

          </Stack>

          <Stack direction="row" spacing={2} flexWrap="wrap">
            <FormControlLabel
              control={
                <Checkbox
                  checked={hideWeekends}
                  onChange={(e) => setHideWeekends(e.target.checked)}
                />
              }
              label="Hide weekends"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={hideRulesInfos}
                  onChange={(e) => setHideRulesInfos(e.target.checked)}
                />
              }
              label="Hide rules info rows"
            />
          </Stack> */}
          

          {/* {(loading || machineLoading) && (
            <Stack direction="row" spacing={1} alignItems="center">
              <CircularProgress size={22} />
              <Typography variant="body2">Loading...</Typography>
            </Stack>
          )} */}

         {/* <Box ref={printAreaRef}>
            <AttendanceTable
              refContainer={containerRef}
              monthData={monthData}
              filteredKeys={displayedKeys}
              visibleDays={visibleDays}
              hideRulesInfos={hideRulesInfos}
              selectedLeaves={selectedLeaves}
              selectedTimingFilter={selectedTimingFilter}
              leaveOptionsCache={leaveOptionsCache}
              restLeavesByEmp={restLeavesByEmp}
              onCellDoubleClick={openEditDialog}
              reportSettings={reportSettings}
            />
          </Box>
        </Stack> */}
      </Paper>

      <EditTimeDialog
        open={dlgOpen}
        onClose={closeEditDialog}
        empName={dlgEmpName}
        date={dlgIso}
        address={dlgAddress}
        initialTimes={dlgInitialTimes}
        leaveOptions={leaveOptionsCache}
        onSave={handleDlgSaveLocal}
        empId={dlgEmpId}
        isoDate={dlgIso}
        cellSegments={dlgCellSegments}
        stampModules={dlgStampModules}
      />

      <Snackbar
        open={toast.open}
        autoHideDuration={2500}
        onClose={() => setToast((p) => ({ ...p, open: false }))}
      >
        <Alert
          severity={toast.sev}
          onClose={() => setToast((p) => ({ ...p, open: false }))}
          variant="filled"
        >
          {toast.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}